import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from '../services/config.service';
import { LanguageStorageService } from './languageStorage.service';
import { environment } from '../../environments/environment';

import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private lang: any;
  private languageChanged = new BehaviorSubject<void>(null); // Or new Subject<void>()
  languages: { [key: string]: string } = {};
  langs: any[] = [ 
    { value: 'Arabic (AR)', key: 'ar' },
    { value: 'Chinese traditional (ZH-TW)', key: 'zh-TW' },
    { value: 'Chinese simplified (ZH-CN)', key: 'zh-CN' },
    { value: 'English (US)', key: 'en' }, 
    { value: 'Filipino (FIL)', key: 'fil' }, 
    { value: 'French (FR)', key: 'fr' },
    { value: 'German (DE)', key: 'de' },
    { value: 'Hindi (HI)', key: 'hi' },
    { value: 'Italian (IT)', key: 'it' },
    { value: 'Japanese (JA)', key: 'ja' },
    { value: 'Korean (KO)', key: 'ko' },
    { value: 'Portuguese (PT)', key: 'pt' },
    { value: 'Russian (RU)', key: 'ru' },
    { value: 'Spanish (ES)', key: 'es' },
    { value: 'Urdu (UR)', key: 'ur' },
    { value: 'Vietnamese (VI)', key: 'vi' }
  ];
  translator: any;

  // Caches keyed by language code -> (key/text -> translation/raw)
  translationsCache: Map<string, Map<string, string>> = new Map();
  keyCache: Map<string, Map<string, string>> = new Map();
  currentLanguage: string = 'en-US';
  
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private http: HttpClient,
    private config: ConfigService,
    private languageStorage: LanguageStorageService
  ) {
    this.langs.forEach(lang => {
      this.languages[lang.key] = lang.value;
    });
  }

  private translatorInitPromise: Promise<any> | null = null;
  private translatorDisabled: boolean = false;
  private _initCalls = 0;


  /* 
   * Create a batching translator that queues translation requests and processes them 
   * together to reduce API calls. This is a simple implementation that waits for a 
   * short delay after the first request before sending all queued requests to the 
   * translator. Adjust the batching logic as needed for your specific translator API 
   * and requirements.
   */
  private pendingTranslations: { text: string, defaultValue: string, promise: Promise<string>, resolve: (value: string) => void }[] = [];
  private batchingDelay = 300;
  private isProcessingBatch = false;
  private translationPromiseCache: Map<string, Promise<string>> = new Map();
  private batchIntervalId: any = null;


  private async initTranslator(sourceLanguage: string = "en", targetLanguage: string = "es"): Promise<any> {
    const
      translateLangs = (environment as any).api.translateLangs,
      translateUrl = (environment as any).api.translateUrl,
      libreFetch = () => fetch(translateLangs, {  })
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((supportedLanguages: any[]) => {
          const 
            isSupported = supportedLanguages.some(lang => lang.code === targetLanguage);
          if (this.batchIntervalId) {
            clearInterval(this.batchIntervalId);
            this.batchIntervalId = null;
          }
          this.batchIntervalId = setInterval(async () => {
            if (this.isProcessingBatch || this.pendingTranslations.length === 0) return;
            this.isProcessingBatch = true;
            // Take a snapshot of the current batch
            const batch = this.pendingTranslations.slice();
            // Remove them from the queue (but keep any new ones that arrive during the request)
            this.pendingTranslations = this.pendingTranslations.slice(batch.length);
            try {
              console.log(`Processing batch of ${batch.length} translations:`, batch.map(req => req.text));
              const res = await fetch(translateUrl, {
                method: 'POST',
                cache: 'force-cache',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  q: batch.map(req => req.text || req.defaultValue).join("\n"),
                  source: sourceLanguage,
                  target: targetLanguage,
                  format: 'html'
                })
              });
              if (!res.ok) {
                throw new Error('Translation request failed');
              }
              const data = await res.json();
              const translations = data.translatedText.split("\n");
              batch.forEach((req, index) => {
                req.resolve(translations[index] || req.defaultValue || req.text);
                // Remove from promise cache after resolving
                const cacheKey = `${req.text}||${req.defaultValue}`;
                this.translationPromiseCache.delete(cacheKey);
              });
            } catch (err) {
              batch.forEach(req => {
                req.resolve(req.defaultValue || req.text);
                const cacheKey = `${req.text}||${req.defaultValue}`;
                this.translationPromiseCache.delete(cacheKey);
              });
            } finally {
              this.isProcessingBatch = false;
            }
          }, this.batchingDelay);
          ;
          if (!isSupported) {
            throw new Error(`Target language ${targetLanguage} is not supported by LibreTranslate API`);
          }

          // Return a translator object that uses the LibreTranslate API
          const libreTranslator = {
            translate: async (text: string, defaultValue: string = "") => {
              if (!text && !defaultValue) {
                console.error('No text provided for translation');
                return text;
              }

              console.log('Requesting translation for:', text.toLowerCase(), 'with overrides:', this.dictionary_overrides);
              // Deduplication: use a cache key
              const cacheKey = `${text}||${defaultValue}`;

              // Check dictionary overrides first
              if (this.dictionary_overrides && this.dictionary_overrides.hasOwnProperty(text.toLowerCase())) {
                return Promise.resolve(this.dictionary_overrides[text.toLowerCase()]);
              }

              if (this.translationPromiseCache.has(cacheKey)) {
                return this.translationPromiseCache.get(cacheKey);
              }
              let new_request = { text, defaultValue, promise: null as any, resolve: null as any };
              let promise = new Promise<string>((resolve) => {
                new_request.resolve = resolve;
              });
              new_request.promise = promise;
              this.pendingTranslations.push(new_request);
              this.translationPromiseCache.set(cacheKey, promise);
              return promise;
            }
          };
          this.translator = libreTranslator;
          return libreTranslator;
        })
        .catch((err: any) => {
          // Fallback: return a dummy translator if fetch fails
          console.warn("LibreTranslate API error, using fallback:", err);
          const fallback = { translate: async (text: string, defaultValue: string = "") => defaultValue || text };
          this.translator = fallback;
          return fallback;
        })
      ;
    ;

    this._initCalls++;

    if (this.translator) {
      return Promise.resolve(this.translator);
    }
    console.log("Initializing translator:", sourceLanguage, "->", targetLanguage);

    // // If disabled via runtime flag or programmatic call, use fallback immediately
    // if ((typeof window !== 'undefined' && ((window as any).__DISABLE_TRANSLATOR__ || (window as any).DISABLE_TRANSLATOR)) || this.translatorDisabled) {
    //   const fallback = { translate: async (text: string) => text };
    //   this.translatorInitPromise = Promise.resolve(fallback);
    //   this.translator = fallback;
    //   console.warn('Translator initialization skipped (disabled). Using fallback translator.');
    //   return this.translatorInitPromise;
    // }

    // debugger;
    if (!this.translatorInitPromise) {
      if (
        targetLanguage !== "en"
      ) {
        if (
          false && 
          (window as any).Translator && 
          typeof (window as any).Translator.create === 'function'
        ) {
          this.translatorInitPromise = (window as any).Translator.create({
            sourceLanguage,
            targetLanguage,
            monitor(monitor: any) {
              monitor.addEventListener("downloadprogress", (e: any) => {
                console.log(`Downloaded ${Math.floor(e.loaded * 100)}%`);
              });
            },
          })
          .then((translator: any) => {
            // throw new Error('Simulated failure to test LibreTranslate fallback');
            this.translator = translator;
            console.log("Translator initialized", this.translator);
            return translator;
          })
          .catch((err: any) => {
            // Fallback: return a dummy translator if creation fails (e.g., NotSupportedError)
            console.warn("Translator API error, using fallback:", err);
            const fallback = { translate: async (text: string, defaultValue: string = "") => defaultValue || text };
            this.translator = fallback;
            // return fallback;
            return libreFetch();
          });
        }

        // Try to use the LibreTranslate API at translate-develop.i-plan.us
        else {
          this.translatorInitPromise = libreFetch();
        }
      }
      else {
        // Fallback: return a dummy translator that just returns the input text
        const fallback = { translate: async (text: string) => text };
        this.translatorInitPromise = Promise.resolve(fallback);
        this.translator = fallback;
        console.warn("Translator API not available, using fallback.");
      }
    }
    return this.translatorInitPromise;
  }

  /**
   * Programmatically enable/disable the translator subsystem.
   * When disabled, a no-op translator is used that returns input text unchanged.
   * Call this early (before initTranslator is called) to take effect.
   */
  public setDisableTranslator(disabled: boolean): void {
    this.translatorDisabled = !!disabled;
    if (this.translatorDisabled) {
      const fallback = { translate: async (text: string) => text };
      this.translatorInitPromise = Promise.resolve(fallback);
      this.translator = fallback;
      console.warn('Translator has been programmatically disabled; using fallback translator.');
    }
    else {
      // Clear state so initTranslator will attempt initialization when called next
      this.translatorInitPromise = null;
      this.translator = null;
      console.info('Translator has been re-enabled; next init will attempt real initialization.');
    }
  }

  dictionary_overrides: { [key: string]: string } = { };
  public async load(language: string = "en-US"): Promise<any> {
    const storedLanguage = this.languageStorage.getLanguage();
    const headers = (new HttpHeaders()).append("X-No-Auth", "True");

    // Initialize translator with dynamic source/target
    console.log("Language: ", language)
    // debugger;
    this.currentLanguage = language;
    await this.initTranslator("en", language);

    return new Promise((resolve, reject) => {
      this.http.get(this.config.get("api.url") + "/langs/en-US", { headers }).subscribe(
        (data: any[]) => {
          console.info("LANGUAGE", data);
          this.lang = data;
          this.languageChanged.next(null);

          this.http
            .get('/assets/lang/' + language + '.json', {  })
            .subscribe(
              (langData: any) => {
                console.info("Loaded language file for", language, langData);
                this.dictionary_overrides = langData;
              },
              (err) => {
                console.error("Error loading language file for", language, err);
                this.dictionary_overrides = {};
              },
              () => {
                resolve({});
              }
            )
          ;
        },
        err => {
          console.error("Error fetching languages", err);
          reject(err);
        },
      );
    });
  }

  public async setLanguage(language: string): Promise<any> {
    this.languageStorage.setLanguage(language);
    this.translatorInitPromise = null; // Reset to allow re-initialization
    console.log("Setting language to:", language);
    this.currentLanguage = language;
    this.clearTranslationCacheForLanguage();
    // return await this.initTranslator("en", language);
  }

  private getNestedValue(data: any, keysString: any) {
    try {
      const keys = keysString.split(".");
      let current = data;

      for (const key of keys) {
        current = current[key];
      }

      return current;
    }
    catch (error) {
      return undefined; // Or throw an error if you prefer
    }
  }

  public getLang(): any[] {
    return this.lang;
  }

  /**
   * Interpolates placeholders in the form {{key}} in the given value using the params object.
   * Example: interpolate('Hello {{name}}', { name: 'World' }) => 'Hello World'
   */
  public interpolate(value: string, params?: { [key: string]: any }): string {
    if (!value) return value;
    // Replace both {{key}} and [[ key ]] syntaxes
    const interpolateOne = (str: string, paramsObj?: { [key: string]: any }): string => {
      return str.replace(/({{\s*([\w.]+)\s*}}|\[\[\s*([\w.]+)\s*\]\])/g, (match, curlyKey, curlyName, squareName) => {
        const key = curlyName || squareName;
        let v = paramsObj && key in paramsObj ? paramsObj[key] : undefined;
        // If not in params, try to resolve from translation file
        if (v === undefined && this.lang) {
          v = this.getNestedValue(this.lang, key);
          // If the looked-up value is a string and contains placeholders, recursively interpolate
          if (typeof v === 'string' && /({{\s*[\w.]+\s*}}|\[\[\s*[\w.]+\s*\]\])/.test(v)) {
            v = interpolateOne(v, paramsObj);
          }
        }
        return v != null ? String(v) : match;
      });
    };
    return interpolateOne(value, params);
  }

  public async get(wh: string, defaultValue: string = ""): Promise<string> {
    if (!this.lang) {
      return defaultValue;
    }

    const keyMap = this.getKeyCacheForCurrentLanguage();

    // 1. Lookup the raw value from en-US (this.lang)
    let this_value = this.getNestedValue(this.lang, wh);

    // 2. Fully interpolate all placeholders in English (en-US) before translation
    this_value = this.interpolate(this_value, defaultValue ? { defaultValue } : undefined);

    // 3. Only then, send the fully composed English string to the translation service
    this_value = await this.translate(this_value, defaultValue);

    keyMap.set(wh, this_value);
    console.log("Key translated: ", wh, "->", this_value);
    return this_value;
  }

  public async translate(text: string, defaultValue: string = ""): Promise<string> {

    console.log("Translating text:", text, "with default:", defaultValue);
    const transMap = this.getTranslationsCacheForCurrentLanguage();
    // if (transMap.has(text)) {
    //   console.log("Translation found in cache:", transMap.get(text));
    //   // Return synchronously if already cached
    //   return Promise.resolve(transMap.get(text));
    // }

    if (!this.translator) {
      console.error("Translator not initialized");
      return defaultValue || text;
    }

    // const placeholder = this.dictionary_overrides['translating'] || "Translating...";
    const 
      translation: any = await this.translator.translate(text, defaultValue)
    ;
    transMap.set(text || defaultValue, translation);
    return translation;
  }

  private getTranslationsCacheForCurrentLanguage(): Map<string, string> {
    const lang = this.currentLanguage || 'en';
    let m = this.translationsCache.get(lang);
    if (!m) {
      m = new Map<string, string>();
      this.translationsCache.set(lang, m);
    }
    return m;
  }

  private getKeyCacheForCurrentLanguage(): Map<string, string> {
    const lang = this.currentLanguage || 'en';
    let m = this.keyCache.get(lang);
    if (!m) {
      m = new Map<string, string>();
      this.keyCache.set(lang, m);
    }
    return m;
  }

  public translateKey(wh: string, defaultValue: string = ""): Promise<string> {
    return this.get(wh, defaultValue).then(rawValue => {
      console.log("Translating key:", wh, "with raw value:", rawValue);
      return this.translate(rawValue, defaultValue);
    });
  }

  public getLanguageChange(): Observable<void> {
    return this.languageChanged.asObservable();
  }

  public clearTranslationCacheForLanguage(): void {
    const lang = this.currentLanguage || 'en';
    // let m = this.translationsCache.get(lang);
    this.translationsCache.clear();
    // console.debug("Getting translations cache for language:", lang, "Cache exists:", !!m);
    // debugger;
    // if (m) {
    //   console.debug("Clearing translations cache for language:", lang);
    //   m.clear();
    // }
    let m = new Map<string, string>();
    this.translationsCache.set(lang, m);
    console.debug("Translations cache after clearing for language:", lang, m.entries ? Array.from(m.entries()) : m);
    // m = this.translationsCache.get(lang);
  }

  public loadLanguages(): Promise<void> {
    // let langs = [ { key: 'en', value: 'English (US)' }, { key: 'es', value: 'Spanish (ES)' } ];
    return new Promise((resolve) => {
      // langs.forEach(lang => {
      //   this.languages[lang.key] = lang.value;
      // });
      resolve();
    });
    // const headers = (new HttpHeaders()).append("X-No-Auth", "True");
    // return new Promise((resolve, reject) => {
    //   this.http.get<{ [key: string]: string }>(this.config.get("api.url") + "/langs", { headers })
    //     .subscribe(
    //       (data) => {
    //         this.languages = data;
    //         resolve();
    //       },
    //       (err) => {
    //         console.error("Error fetching supported languages", err);
    //         reject(err);
    //       }
    //     );
    // });
  }

  detectBrowserLanguage(): string {
    if (isPlatformBrowser(this.platformId)) {
      const 
        storedLanguage = this.languageStorage.getLanguage()
      ;
      if (storedLanguage && this.languages[storedLanguage]) {
        return storedLanguage;
      }

      let browserLanguage = navigator.language;
      if (this.languages[browserLanguage]) {
        return browserLanguage;
      }
      else {
        const langWithoutRegion = browserLanguage.split('-')[0];
        if (this.languages[langWithoutRegion]) {
          return langWithoutRegion;
        }
        else {
          return browserLanguage;
        }
      }
    }
    return 'en-US'; // Default for server-side rendering or if navigator is not available.
  }
}
